import { useQueries } from "@tanstack/react-query";
import {
  normalizeThreadChunkAttachments,
  normalizeThreadStateAttachments,
} from "@/entities/thread/normalizeAttachments";
import type {
  ThreadChunkPost,
  ThreadPostState,
  ThreadState,
} from "@/entities/thread/types";
import { isThreadChunkPost } from "@/entities/thread/types";
import { THREAD_CHUNK_SIZE } from "@/features/thread/hooks/threadChunkQuery";
import { apiGet, hasAimgToken, useAimgViewerContext } from "@/shared/api";
import { API_VERSION } from "@/shared/api/version";

/**
 * リストモードの1ページあたりのスレッド件数。
 * カタロググリッドのcolumns×rows(既定56、最大100)から独立した固定値とする。
 * グリッド設定に連動させると旧/top(10件)から大幅増となり、スレ毎のtail取得
 * (useBoardThreadTails)も同倍増して負荷とUXを悪化させるため(#42 レビュー指摘)、
 * 負荷とUXのバランスを取れる値として20に固定する。
 */
export const LIST_PAGE_LIMIT = 20;

export interface BoardThreadTail {
  threadId: string;
  posts: ThreadChunkPost[];
}

export function getBoardTailChunkNumbers(
  postStates: ThreadPostState[],
): number[] {
  const latestPublicReplySeqs = postStates
    .filter((postState) => postState.seq > 0 && postState.status === "public")
    .map((postState) => postState.seq)
    .sort((left, right) => left - right)
    .slice(-8);

  return [
    ...new Set(
      latestPublicReplySeqs.map((seq) => Math.floor(seq / THREAD_CHUNK_SIZE)),
    ),
  ].sort((left, right) => left - right);
}

export function useBoardThreadTails(threadIds: string[]): {
  tails: Map<string, BoardThreadTail>;
  isFetching: boolean;
  hasError: boolean;
} {
  const viewerContext = useAimgViewerContext();
  const queries = useQueries({
    queries: threadIds.map((threadId) => ({
      queryKey: ["board-thread-tail", threadId, "viewer", viewerContext],
      queryFn: async ({
        signal,
      }: {
        signal: AbortSignal;
      }): Promise<BoardThreadTail> => {
        const hasViewerToken = hasAimgToken();
        const state = normalizeThreadStateAttachments(
          await apiGet<ThreadState>(
            `/threads/${encodeURIComponent(threadId)}/state`,
            {
              signal,
              cache: "no-store",
              requiresToken: hasViewerToken,
              allowTokenIssue: false,
            },
          ),
        );
        const publicSeqs = new Set(
          state.postStates
            .filter((postState) => postState.status === "public")
            .map((postState) => postState.seq),
        );
        const chunks = await Promise.all(
          getBoardTailChunkNumbers(state.postStates).map((chunkNumber) =>
            apiGet<
              Array<ThreadChunkPost | { seq: number; status: "unavailable" }>
            >(
              hasViewerToken
                ? `/threads/${encodeURIComponent(threadId)}/chunks/${chunkNumber}`
                : `/threads/${encodeURIComponent(threadId)}/public-chunks/${chunkNumber}?apiVersion=${encodeURIComponent(API_VERSION)}`,
              {
                signal,
                cache: "no-store",
                requiresToken: hasViewerToken,
                allowTokenIssue: false,
              },
            ),
          ),
        );
        return {
          threadId,
          posts: normalizeThreadChunkAttachments(chunks.flat())
            .filter(isThreadChunkPost)
            .filter((post) => post.seq > 0 && publicSeqs.has(post.seq))
            .sort((left, right) => left.seq - right.seq)
            .slice(-8),
        };
      },
      enabled: Boolean(threadId),
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    })),
  });

  const tails = new Map<string, BoardThreadTail>();
  for (const query of queries) {
    if (query.data) tails.set(query.data.threadId, query.data);
  }
  return {
    tails,
    isFetching: queries.some((query) => query.isFetching),
    hasError: queries.some((query) => query.isError),
  };
}
